'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import {
  CheckCircle2,
  RefreshCw,
  Search,
  Phone,
  User,
  Calendar,
  Megaphone,
  Layers,
  TrendingUp,
  MessageCircle,
  Eye,
  ChevronLeft,
  ChevronRight,
  Download,
  History,
} from 'lucide-react';
import LeadMessageHistoryModal from '@/components/LeadMessageHistoryModal';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DeliveredMessage {
  id: string;
  delivered_at: string;
  phone: string;
  lead_name: string | null;
  source: string | null;
  campaign: string | null;
  template_name: string | null;
  status: 'delivered' | 'read';
  message_sid: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const SOURCE_COLORS: Record<string, string> = {
  'MBBS Lighthouse': '#f59e0b',
  'GMC': '#8b5cf6',
  'Neet Navigator': '#06b6d4',
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, '0');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const month = months[d.getMonth()];
  const year = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${day} ${month} ${year}, ${hh}:${mm}`;
}

function formatDateShort(iso: string): string {
  const d = new Date(iso);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[d.getMonth()]} ${d.getDate()}`;
}

const PAGE_SIZE = 20;

// ─── Component ────────────────────────────────────────────────────────────────

export default function SuccessfulDeliveriesClient() {
  const supabase = createClient();

  const [messages, setMessages] = useState<DeliveredMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'delivered' | 'read'>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [historyLead, setHistoryLead] = useState<{ phone: string; name: string | null } | null>(null);

  // KPI state
  const [kpis, setKpis] = useState({
    total: 0,
    today: 0,
    readCount: 0,
    sourcesReached: 0,
  });

  useEffect(() => { setMounted(true); }, []);

  const fetchData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      // Use a 60-day lookback for chat_messages
      const sixtyDaysAgo = new Date();
      sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);
      const sixtyDaysAgoISO = sixtyDaysAgo.toISOString();

      // Fetch delivered/read messages from chat_messages (left join — no !inner)
      const { data: chatData, error: chatError } = await supabase
        .from('chat_messages')
        .select(`
          id,
          created_at,
          status,
          template_name,
          direction,
          leads(
            name,
            phone,
            source
          )
        `)
        .in('status', ['delivered', 'read'])
        .eq('direction', 'out')
        .gte('created_at', sixtyDaysAgoISO)
        .order('created_at', { ascending: false })
        .limit(5000);

      if (chatError) {
        console.error('[SuccessfulDeliveries] chat_messages error:', chatError);
      }

      // Fetch ALL sent drip messages — no date filter so June 20/21 records
      // are never excluded by a lookback window. Sort by processed_at desc.
      const { data: dripData, error: dripError } = await supabase
        .from('drip_message_queue')
        .select('id, lead_phone, campaign_id, message_sid, processed_at, scheduled_at, created_at, lead_source, lead_name')
        .eq('status', 'sent')
        .order('scheduled_at', { ascending: false })
        .limit(5000);

      if (dripError) {
        console.error('[SuccessfulDeliveries] drip_message_queue error:', dripError);
      }

      // Build a set of message_sids already in chat_messages to avoid true duplicates
      const chatSids = new Set(
        (chatData ?? []).map((msg: any) => msg.message_sid).filter(Boolean)
      );

      // Normalize chat messages
      const normalized: DeliveredMessage[] = (chatData ?? []).map((msg: any) => {
        const lead = Array.isArray(msg.leads) ? msg.leads[0] : msg.leads;
        const phone = lead?.phone ?? '';
        return {
          id: msg.id,
          delivered_at: msg.created_at,
          phone,
          lead_name: lead?.name ?? null,
          source: lead?.source ?? null,
          campaign: null,
          template_name: msg.template_name ?? null,
          status: msg.status as 'delivered' | 'read',
          message_sid: msg.message_sid ?? null,
        };
      });

      // Include ALL drip sent messages as individual rows.
      // Only skip a drip entry if its message_sid already exists in chat_messages
      // (meaning Twilio already delivered a status callback for it).
      const dripRows: DeliveredMessage[] = (dripData ?? [])
        .filter((d) => {
          if (!d.lead_phone) return false;
          // Skip if this exact message_sid is already in chat_messages
          if (d.message_sid && chatSids.has(d.message_sid)) return false;
          return true;
        })
        .map((d) => {
          // Use processed_at first, then scheduled_at, then created_at
          const deliveredAt = d.processed_at ?? d.scheduled_at ?? d.created_at;
          if (!deliveredAt) return null;
          return {
            id: `drip-${d.id}`,
            delivered_at: deliveredAt,
            phone: d.lead_phone,
            lead_name: d.lead_name ?? null,
            source: d.lead_source ?? null,
            campaign: d.campaign_id ?? null,
            template_name: null,
            status: 'delivered' as const,
            message_sid: d.message_sid ?? null,
          };
        })
        .filter((d): d is DeliveredMessage => d !== null);

      const all = [...normalized, ...dripRows].sort(
        (a, b) => new Date(b.delivered_at).getTime() - new Date(a.delivered_at).getTime()
      );

      setMessages(all);

      // Compute KPIs
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
      const todayMsgs = all.filter((m) => new Date(m.delivered_at).getTime() >= todayStart);
      const readMsgs = all.filter((m) => m.status === 'read');
      const sources = new Set(all.map((m) => m.source).filter(Boolean));

      setKpis({
        total: all.length,
        today: todayMsgs.length,
        readCount: readMsgs.length,
        sourcesReached: sources.size,
      });
    } catch (err) {
      console.error('[SuccessfulDeliveries] Error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [supabase]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Filtered list ──────────────────────────────────────────────────────────
  const filtered = messages.filter((m) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      m.phone.toLowerCase().includes(q) ||
      (m.lead_name ?? '').toLowerCase().includes(q) ||
      (m.source ?? '').toLowerCase().includes(q) ||
      (m.campaign ?? '').toLowerCase().includes(q);
    const matchStatus = statusFilter === 'all' || m.status === statusFilter;
    const matchSource = sourceFilter === 'all' || m.source === sourceFilter;
    return matchSearch && matchStatus && matchSource;
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Reset page when filters change
  useEffect(() => { setPage(0); }, [search, statusFilter, sourceFilter]);

  // ── Sources for filter ─────────────────────────────────────────────────────
  const allSources = Array.from(new Set(messages.map((m) => m.source).filter(Boolean))) as string[];

  // ── Export CSV ─────────────────────────────────────────────────────────────
  const handleExport = () => {
    const headers = ['Lead Name', 'Phone', 'Source', 'Campaign', 'Template', 'Status', 'Delivered At'];
    const rows = filtered.map((m) => [
      m.lead_name ?? '-',
      m.phone,
      m.source ?? '-',
      m.campaign ?? '-',
      m.template_name ?? '-',
      m.status,
      m.delivered_at,
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'successful-deliveries.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Trend data (last 7 days) ───────────────────────────────────────────────
  const trendData = mounted
    ? (() => {
        const days: { label: string; count: number }[] = [];
        for (let i = 6; i >= 0; i--) {
          const d = new Date();
          d.setDate(d.getDate() - i);
          const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
          const dayEnd = dayStart + 86400000;
          const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
          days.push({
            label: `${months[d.getMonth()]} ${d.getDate()}`,
            count: messages.filter((m) => {
              const t = new Date(m.delivered_at).getTime();
              return t >= dayStart && t < dayEnd;
            }).length,
          });
        }
        return days;
      })()
    : [];

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full bg-background min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-surface shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-green-500/15 flex items-center justify-center">
            <CheckCircle2 size={20} className="text-green-500" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-foreground leading-tight">Successful Deliveries</h1>
            <p className="text-xs text-muted-foreground">WhatsApp messages delivered or read by leads</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExport}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-muted-foreground border border-border rounded-lg hover:bg-surface-hover transition-colors"
          >
            <Download size={13} />
            Export CSV
          </button>
          <button
            onClick={() => fetchData(true)}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-muted-foreground border border-border rounded-lg hover:bg-surface-hover transition-colors disabled:opacity-50"
          >
            <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-surface border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <MessageCircle size={16} className="text-green-500" />
              <span className="text-xs font-medium text-muted-foreground">Total Delivered</span>
            </div>
            <p className="text-2xl font-bold text-foreground">
              {loading ? '—' : kpis.total.toLocaleString()}
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5">All time</p>
          </div>
          <div className="bg-surface border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Calendar size={16} className="text-blue-500" />
              <span className="text-xs font-medium text-muted-foreground">Delivered Today</span>
            </div>
            <p className="text-2xl font-bold text-foreground">
              {loading ? '—' : mounted ? kpis.today : '—'}
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5">Since midnight</p>
          </div>
          <div className="bg-surface border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Eye size={16} className="text-purple-500" />
              <span className="text-xs font-medium text-muted-foreground">Read by Lead</span>
            </div>
            <p className="text-2xl font-bold text-foreground">
              {loading ? '—' : kpis.readCount}
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5">Status: read</p>
          </div>
          <div className="bg-surface border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Layers size={16} className="text-amber-500" />
              <span className="text-xs font-medium text-muted-foreground">Sources Reached</span>
            </div>
            <p className="text-2xl font-bold text-foreground">
              {loading ? '—' : kpis.sourcesReached}
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5">Unique sources</p>
          </div>
        </div>

        {/* Trend Bar */}
        {mounted && trendData.length > 0 && (
          <div className="bg-surface border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp size={15} className="text-green-500" />
              <span className="text-sm font-semibold text-foreground">Delivery Trend — Last 7 Days</span>
            </div>
            <div className="flex items-end gap-2 h-16">
              {trendData.map((d, i) => {
                const max = Math.max(...trendData.map((x) => x.count), 1);
                const pct = (d.count / max) * 100;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-[10px] text-muted-foreground font-medium">{d.count || ''}</span>
                    <div className="w-full rounded-t-sm bg-green-500/20 relative" style={{ height: '40px' }}>
                      <div
                        className="absolute bottom-0 left-0 right-0 rounded-t-sm bg-green-500 transition-all duration-500"
                        style={{ height: `${pct}%` }}
                      />
                    </div>
                    <span className="text-[9px] text-muted-foreground">{d.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by name, phone, source, campaign…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-sm bg-surface border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-green-500/30"
            />
          </div>
          {/* Status filter */}
          <div className="flex items-center gap-1 bg-surface border border-border rounded-lg p-1">
            {(['all', 'delivered', 'read'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors capitalize ${
                  statusFilter === s
                    ? 'bg-green-500 text-white' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                {s === 'all' ? 'All Status' : s}
              </button>
            ))}
          </div>
          {/* Source filter */}
          {allSources.length > 0 && (
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="px-3 py-2 text-xs bg-surface border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-green-500/30"
            >
              <option value="all">All Sources</option>
              {allSources.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          )}
          <span className="text-xs text-muted-foreground ml-auto">
            {filtered.length} record{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Table */}
        <div className="bg-surface border border-border rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw size={20} className="animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Loading deliveries…</span>
            </div>
          ) : paginated.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <CheckCircle2 size={36} className="text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">No delivered messages found</p>
              {(search || statusFilter !== 'all' || sourceFilter !== 'all') && (
                <button
                  onClick={() => { setSearch(''); setStatusFilter('all'); setSourceFilter('all'); }}
                  className="text-xs text-green-500 hover:underline"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-surface-hover">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Lead</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Phone</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Source</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Campaign</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Template</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Status</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Delivered At</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {paginated.map((msg) => (
                      <tr key={msg.id} className="hover:bg-surface-hover transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-green-500/15 flex items-center justify-center shrink-0">
                              <User size={13} className="text-green-600" />
                            </div>
                            <button
                              onClick={() => setHistoryLead({ phone: msg.phone, name: msg.lead_name })}
                              className="font-medium text-foreground text-sm hover:text-green-600 hover:underline transition-colors text-left"
                            >
                              {msg.lead_name ?? <span className="text-muted-foreground italic">Unknown</span>}
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5 text-muted-foreground">
                            <Phone size={12} />
                            <span className="text-xs font-mono">{msg.phone}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {msg.source ? (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold"
                              style={{
                                backgroundColor: `${SOURCE_COLORS[msg.source] ?? '#6b7280'}20`,
                                color: SOURCE_COLORS[msg.source] ?? '#6b7280',
                              }}
                            >
                              {msg.source}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {msg.campaign ? (
                            <div className="flex items-center gap-1.5">
                              <Megaphone size={12} className="text-muted-foreground shrink-0" />
                              <span className="text-xs text-foreground truncate max-w-[120px]">{msg.campaign}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {msg.template_name ? (
                            <span className="text-xs text-foreground bg-surface-hover px-2 py-0.5 rounded font-mono">
                              {msg.template_name}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {msg.status === 'read' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-purple-500/10 text-purple-600">
                              <Eye size={10} />
                              Read
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-500/10 text-green-600">
                              <CheckCircle2 size={10} />
                              Delivered
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {mounted ? formatDate(msg.delivered_at) : '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => setHistoryLead({ phone: msg.phone, name: msg.lead_name })}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-muted-foreground border border-border rounded-lg hover:bg-surface-hover hover:text-foreground transition-colors"
                            title="View full message history"
                          >
                            <History size={11} />
                            History
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                  <span className="text-xs text-muted-foreground">
                    Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} of {filtered.length}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      disabled={page === 0}
                      className="p-1.5 rounded-lg border border-border text-muted-foreground hover:bg-surface-hover disabled:opacity-40 transition-colors"
                    >
                      <ChevronLeft size={14} />
                    </button>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const pageNum = Math.max(0, Math.min(page - 2, totalPages - 5)) + i;
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setPage(pageNum)}
                          className={`w-7 h-7 text-xs rounded-lg border transition-colors ${
                            pageNum === page
                              ? 'bg-green-500 text-white border-green-500' :'border-border text-muted-foreground hover:bg-surface-hover'
                          }`}
                        >
                          {pageNum + 1}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                      disabled={page >= totalPages - 1}
                      className="p-1.5 rounded-lg border border-border text-muted-foreground hover:bg-surface-hover disabled:opacity-40 transition-colors"
                    >
                      <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {historyLead && (
        <LeadMessageHistoryModal
          phone={historyLead.phone}
          leadName={historyLead.name}
          onClose={() => setHistoryLead(null)}
        />
      )}
    </div>
  );
}
